import React, { useEffect, Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LTCommonHeader from "@lambdatestincprivate/lt-common-header";
import "@lambdatestincprivate/lt-components/lib/index.css";
import "@lambdatestincprivate/lt-common-header/build/styles.css";
import {
  LTIconButton,
  LTTheme,
  LTThemeProvider,
  LTToast
} from "@lambdatestincprivate/lt-components";
import get from "lodash/get";

import { fetchFeatureFlags } from "../../redux/actions/userInfoActions";
import { fetchOrgPreferences } from "../../redux/actions/orgPreferencesActions";
import { EventsMap } from "../../common/constants/events";
import {
  getUrlByUserDomain,
  isHyExOnPremDomain,
  isSharingRoute,
  sendAmplitudeEvents
} from "../../utility/utilityFunctions";

import App from "../App";

import styles from "./index.module.css";

import AccessKeyPopUp from "../../components/ml2.1/AccessKeyPopUp";
import Analytics from "../../images/analytics";
import AccessKey from "../../images/access-key-top-nav";

const SharingParentWrapper = React.lazy(() =>
  import("../../components/SharingParentWrapper")
);

/**
 * Displays queued concurrency for RBAC.
 * @returns {string} Text to display
 */
function getQueuedConcurrencyTitle(lambdaTestConcurrency) {
  if (
    lambdaTestConcurrency &&
    lambdaTestConcurrency.data &&
    lambdaTestConcurrency.data.queued &&
    lambdaTestConcurrency.data.maxQueue
  ) {
    return `${parseInt(lambdaTestConcurrency.data.queued)}/${
      lambdaTestConcurrency.data.maxQueue
    } Queued`;
  } else {
    return `0/${
      lambdaTestConcurrency &&
      lambdaTestConcurrency.data &&
      lambdaTestConcurrency.data.maxQueue
        ? lambdaTestConcurrency.data.maxQueue
        : 0
    } Queued`;
  }
}

const routesWithoutHeader = ["testLogs"];
const PreApp = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userInfo.userInfo);
  const lambdaTestConcurrency = useSelector(
    state => state.lambdaTestConcurrencyReducer.lambdaTestConcurrency
  );
  const featureFlags = useSelector(state => state.userInfo.featureFlags);

  const { organization } = userInfo?.data || {};
  const { id: userOrgId } = organization || {};  

  const slicedPathname = window.location.pathname.split("/");
  const firstPath = slicedPathname[1];
  const isExternalRoute = firstPath === "public";
  const isLiveVideo = slicedPathname[2] === "live-video";
  const shouldRemoveCommonHeader =
    isExternalRoute ||
    firstPath === "share" ||
    firstPath === "playwright-trace" ||
    firstPath === "lighthouse-report" ||
    routesWithoutHeader.includes(firstPath);
  const shouldShowConcurrency =
    window.location.pathname === "/" ||
    window.location.pathname.includes("/build") ||
    window.location.pathname === "/test";
  const isGroupRuleApplied =
    lambdaTestConcurrency &&
    lambdaTestConcurrency.data &&
    lambdaTestConcurrency.data.ruleApplied === "group";
  let greenTitle = (
    <span data-place="bottom" data-tip="Running tests in the cloud">
      Parallel
    </span>
  );
  let orangeTitle = (
    <span data-place="bottom" data-tip="Waiting for test execution">
      Queued
    </span>
  );
  const desktopRunningCount = get(
    lambdaTestConcurrency,
    ["data", "running"],
    0
  );
  const realMobileRunningCount = get(
    lambdaTestConcurrency,
    ["data", "realMobileRunning"],
    0
  );
  const automationMaxConcurrency = get(
    lambdaTestConcurrency,
    ["data", "maxAutomationConcurrency"],
    0
  );
  const maxPlatformConcurrency = get(
    lambdaTestConcurrency,
    ["data", "platformConcurrency"],
    0
  );
  const realMobileMaxConcurrency = get(
    lambdaTestConcurrency,
    ["data", "maxAutomationRealMobileConcurrency"],
    0
  );
  let greenRunningCount = Math.min(
    desktopRunningCount + realMobileRunningCount,
    maxPlatformConcurrency
  );
  const totalConcurrencyToShow = Math.min(
    realMobileMaxConcurrency + automationMaxConcurrency,
    maxPlatformConcurrency
  );
  let greenTotalCount = totalConcurrencyToShow || 0;
  let orangeRunningCount =
    (lambdaTestConcurrency &&
      lambdaTestConcurrency.data &&
      lambdaTestConcurrency.data.queued) ||
    0;
  let orangeTotalCount =
    (lambdaTestConcurrency.data && lambdaTestConcurrency.data.maxQueue) || 0;
  if (isGroupRuleApplied) {
    const tooltipToDisplay = getQueuedConcurrencyTitle(lambdaTestConcurrency);
    greenTitle = (
      <span data-place="bottom" data-tip={tooltipToDisplay}>
        Group Tests
      </span>
    );
    orangeTitle = (
      <span data-place="bottom" data-tip={tooltipToDisplay}>
        Org Tests
      </span>
    );
    greenRunningCount =
      (lambdaTestConcurrency.data &&
        lambdaTestConcurrency.data.groupRunningCount) ||
      0;
    greenTotalCount =
      (lambdaTestConcurrency.data &&
        lambdaTestConcurrency.data.groupConcurrency) ||
      0;
    orangeRunningCount =
      (lambdaTestConcurrency.data &&
        lambdaTestConcurrency.data.orgRunningCount) ||
      0;
    orangeTotalCount =
      (lambdaTestConcurrency.data &&
        lambdaTestConcurrency.data.orgConcurrency) ||
      0;
  }

  useEffect(() => {
    if (!isHyExOnPremDomain()) {
      dispatch(fetchFeatureFlags());
      const slicedPathname = window.location.pathname.split("/");
      const firstPath = slicedPathname[1];
      const shouldNotFetchPrefs =
        firstPath === "public" ||
        isSharingRoute() ||
        routesWithoutHeader.includes(firstPath);
      if (!shouldNotFetchPrefs) {
        dispatch(fetchOrgPreferences());
      }
    }
  }, [dispatch]);

  // Org-based redirects (Fannie Mae, Cigna, etc.)
  useEffect(() => {
    if (!userOrgId) return;

    // Fannie Mae redirect
    const fannieMaeOrgIds = process.env.REACT_APP_FANNIEMAE_ORGID?.split(",")?.map(
      Number
    );
    if (
      fannieMaeOrgIds?.includes(userOrgId) &&
      !window.location.hostname.includes("fanniemae-automation")
    ) {
      const currentPathAndQuery =
        window.location.pathname + window.location.search;
      window.location.replace(
        process.env.REACT_APP_FANNIEMAE_URL + currentPathAndQuery
      );
      return;
    }

    // Generic org redirects (e.g. Cigna)
    const envValue = (process.env.REACT_APP_ORG_REDIRECTS || "{}").replace(
      /^['"]|['"]$/g,
      ""
    );
    let orgRedirects;
    try {
      orgRedirects = JSON.parse(envValue);
    } catch {
      return;
    }
    const config = orgRedirects[String(userOrgId)];
    if (config?.host && !window.location.hostname.includes(config.host)) {
      const currentPathAndQuery =
        window.location.pathname + window.location.search;
      window.location.replace(`https://${config.host}${currentPathAndQuery}`);
    }
  }, [userOrgId]);

  const ltAccessibilityFlag = featureFlags?.data?.lt_accessibility_flag;

  const isLtAccessibilityEnabled =
    get(ltAccessibilityFlag, ["variant", "key"], "false") === "on";

  const goToAnalytics = () => {
    sendAmplitudeEvents(EventsMap.BUILD_VIEW_CLICK_ANALYTICS);
    window.location.href = `${getUrlByUserDomain(
      process.env.REACT_APP_ML_ANALYTICS_PREFIX
    )}/test-overview`;
  };

  const onPremPrefixToUse = window?._env_?.REACT_APP_ONPREM_PREFIX || undefined;
  const sendNotification = addToast => {
    window.toast = {
      addToast,
      extendDuration: isLtAccessibilityEnabled,
      toastVariants: LTToast.ToastVariants
    };
  };

  const idleTimeOutMinutes =
    window?._env_?.REACT_APP_IDLE_TIMEOUT_IN_MINUTES ||
    process.env.REACT_APP_IDLE_TIMEOUT_IN_MINUTES ||
    0;
  return (
    <LTThemeProvider
      theme={LTTheme}
      colorMode="light"
      dayScheme={isLtAccessibilityEnabled ? "light_high_contrast" : "light"}
    >
      <LTToast sendNotification={sendNotification} />
      <Router>
        {!shouldRemoveCommonHeader ? (
          <LTCommonHeader
            customProductHeader={
              <h1 className={styles.productTitle}>Automation</h1>
            }
            concurrencyInfo={
              shouldShowConcurrency
                ? [
                    {
                      color: "green",
                      runningCount: greenRunningCount,
                      title: greenTitle,
                      total: greenTotalCount
                    },
                    {
                      color: "orange",
                      runningCount: orangeRunningCount,
                      title: orangeTitle,
                      total: orangeTotalCount
                    }
                  ]
                : []
            }
            onPremPrefixToUse={onPremPrefixToUse}
            showTunnel={true}
            isNewThemeEnabled={true}
            isHighContrastThemeEnabled={isLtAccessibilityEnabled}
            lowResNavItems={[
              {
                icon: <Analytics />,
                label: "Analytics",
                onClick: goToAnalytics
              },
              {
                icon: <AccessKey />,
                label: "Access Key",
                contentToShow: <AccessKeyPopUp renderOnlyInnerContent={true} />
              }
            ]}
            topNavButtons={
              <div className={styles.ltTopNavBtns}>
                {!onPremPrefixToUse && (
                  <LTIconButton
                    aria-label="Go to Analytics"
                    data-testid="go-to-analytics-btn"
                    icon={() => <Analytics />}
                    onClick={goToAnalytics}
                    size={LTIconButton.Size.SMALL}
                  />
                )}
                <AccessKeyPopUp />
              </div>
            }
            timeoutMinutes={idleTimeOutMinutes}
          >
            <section style={{ height: "100%" }}>
              <App />
            </section>
          </LTCommonHeader>
        ) : (
          <section>
            {isExternalRoute ? (
              <Suspense fallback={<></>}>
                <SharingParentWrapper isLiveVideo={isLiveVideo} />
              </Suspense>
            ) : (
              <App />
            )}
          </section>
        )}
      </Router>
    </LTThemeProvider>
  );
};

export default PreApp;
